import React from 'react';
import NavigationBar from '@/components/landing/NavigationBar';
import Footer from '@/components/landing/Footer';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { useEffect } from 'react';

const MarketResearch = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Check if we need to scroll to a specific section
    if (location.state && location.state.scrollToId) {
      const element = document.getElementById(location.state.scrollToId);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else {
      // Otherwise scroll to top
      window.scrollTo(0, 0);
    }
  }, [location]);
  
  return (
    <>
      <Helmet>
        <title>Market Research Solutions | AnalystX</title>
        <meta name="description" content="Comprehensive market research solutions powered by AI to help you understand market trends, customer preferences, and competitive landscapes." />
        <meta name="keywords" content="market research, market intelligence, market trends, customer insights, competitor analysis" />
      </Helmet>
      
      <div className="min-h-screen bg-gray-900 text-white">
        <NavigationBar />
        
        <main className="container mx-auto px-4 py-16">
          <h1 className="text-4xl font-bold mb-6">Market Research Solutions</h1>
          
          <div className="bg-gray-800/50 rounded-xl p-6 mb-12">
            <h2 className="text-2xl font-semibold mb-4">Comprehensive Market Intelligence</h2>
            <p className="text-gray-300 mb-4">
              Our AI-powered market research platform delivers accurate and timely insights to help you make informed business decisions. 
              We analyze vast amounts of data to identify trends, opportunities, and risks in your target markets.
            </p>
            <p className="text-gray-300 mb-4">
              AnalystX transforms how businesses conduct market research, providing enterprise-grade insights at a fraction of the 
              traditional cost and time investment.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-gray-800/30 rounded-xl p-6">
              <h3 className="text-xl font-semibold mb-3">Market Sizing & Forecasting</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-300">
                <li>Total addressable market (TAM) analysis</li>
                <li>Market growth projections</li>
                <li>Segment-specific opportunity sizing</li>
                <li>Regional market comparisons</li>
                <li>Demand forecasting models</li>
                <li>Market penetration strategies</li>
              </ul>
            </div>
            
            <div className="bg-gray-800/30 rounded-xl p-6">
              <h3 className="text-xl font-semibold mb-3">Consumer Insights & Trends</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-300">
                <li>Customer preference analysis</li>
                <li>Buying behavior patterns</li>
                <li>Demographic profiling</li>
                <li>Emerging trend identification</li>
                <li>Consumer sentiment tracking</li>
                <li>Brand perception assessments</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-blue-900/30 rounded-xl p-6 border border-blue-800/50 mb-12">
            <h2 className="text-2xl font-semibold mb-4">Research Methodology</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h3 className="text-lg font-medium mb-2">Data Collection</h3>
                <p className="text-gray-300">Our AI aggregates information from thousands of public and proprietary sources in real-time.</p>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">Analysis</h3>
                <p className="text-gray-300">Advanced algorithms identify patterns, correlations, and insights human analysts might miss.</p>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">Reporting</h3>
                <p className="text-gray-300">Comprehensive yet digestible reports with actionable recommendations for your business.</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-800/50 rounded-xl p-6">
            <h2 className="text-2xl font-semibold mb-4">Why Choose AnalystX</h2>
            <p className="text-gray-300 mb-4">
              Traditional market research takes weeks or months and costs tens of thousands of dollars. AnalystX delivers comparable 
              insights in minutes at a fraction of the cost, without sacrificing quality or depth of analysis.
            </p>
            <p className="text-gray-300">
              Our AI continuously learns from new data, ensuring your research is always based on the most current market information available.
            </p>
          </div>
        </main>
        
        <Footer />
      </div>
    </>
  );
};

export default MarketResearch;
